"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Lock, User, Eye, EyeOff, ShieldCheck, AlertCircle, ArrowRight, Building2, Globe, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";

interface CrmLoginProps {
  onLoginSuccess: (user: {
    id: number;
    username: string;
    role: string;
    fullName: string;
    companyName?: string;
    industry?: string;
    planTier?: string;
  }) => void;
}

export default function CrmLogin({ onLoginSuccess }: CrmLoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [companiesList, setCompaniesList] = useState<Array<{
    id: number;
    username: string;
    companyName: string;
    role: string;
    industry?: string;
  }> | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/crm/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        if (data.requireCompanySelection && data.companies?.length > 1) {
          setCompaniesList(data.companies);
        } else if (data.user) {
          onLoginSuccess(data.user);
        }
      } else {
        setError(data.error || "Invalid username or password.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = async (companyId: number) => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/crm/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          password,
          selectedUserId: companyId,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success && data.user) {
        onLoginSuccess(data.user);
      } else {
        setError(data.error || "Failed to launch company portal.");
      }
    } catch {
      setError("Network error connecting to company workspace.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16 relative">
      {/* Background ambient glow */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="p-8 sm:p-10 rounded-2xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-slate-200/80 dark:border-slate-800/80 shadow-2xl shadow-primary/5">
          {/* Back to Website Redirect Option */}
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100 dark:border-slate-800/60">
            <Link
              href="/"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors py-1 px-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Website</span>
            </Link>
            <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">
              Enterprise CRM
            </span>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-xl border border-cyan-500/30 overflow-hidden mb-4 bg-[#0B132B]">
              <img src="/brand/app-icon.svg?v=3" alt="Johnson Softwares" className="w-full h-full object-contain" />
            </div>
            <h1 className="font-heading font-black text-xl sm:text-2xl tracking-[0.14em] uppercase text-slate-900 dark:text-white">
              JOHNSON <span className="text-cyan-500 dark:text-cyan-400 tracking-[0.18em]">SOFTWARES</span> <span className="text-slate-400 font-semibold text-base sm:text-lg">CRM</span>
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 font-medium">
              Internal Pipeline & Lead Intelligence Portal
            </p>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-xs flex items-start gap-3">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="leading-relaxed">{error}</div>
            </div>
          )}

          {/* Multi-Company Selection or Form */}
          {companiesList && companiesList.length > 0 ? (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center mx-auto mb-2">
                  <Building2 className="w-5 h-5" />
                </div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white">Choose Company Workspace</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Multiple companies are associated with this email. Select which portal to launch:
                </p>
              </div>

              <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                {companiesList.map((comp) => (
                  <button
                    key={comp.id}
                    type="button"
                    disabled={loading}
                    onClick={() => handleSelectCompany(comp.id)}
                    className="w-full p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 hover:bg-cyan-500/5 hover:border-cyan-500/50 dark:bg-slate-950/60 dark:hover:bg-cyan-500/10 transition-all text-left flex items-center justify-between group cursor-pointer"
                  >
                    <div>
                      <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover:text-cyan-600 dark:group-hover:text-cyan-400 flex items-center gap-2">
                        <span>{comp.companyName}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-mono uppercase">
                          {comp.role}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 font-mono">
                        @{comp.username}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-cyan-500 group-hover:translate-x-0.5 transition-all" />
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => {
                  setCompaniesList(null);
                  setError("");
                }}
                className="w-full text-xs text-slate-500 hover:underline pt-2 text-center cursor-pointer"
              >
                ← Sign in with a different username or email
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-2">
                  Username or Email
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <User className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username or email (e.g. alex_smith or user@company.com)"
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Password
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-xs text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Lock className="w-4 h-4" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="w-full pl-10 pr-10 py-2.5 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 mt-6 cursor-pointer shadow-lg shadow-primary/20"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Sign In to CRM</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>

              <a
                href="/register"
                className="w-full py-2.5 px-3 rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-500/20 text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 text-center mt-3 shadow-sm"
              >
                <span>🚀 Start 7-Day Free Trial (Tier 1)</span>
              </a>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-800/60 text-center space-y-2">
            <Link
              href="/"
              className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors"
            >
              <Globe className="w-3.5 h-3.5 text-cyan-500" />
              <span>Explore Johnson Softwares Public Website</span>
            </Link>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              Authorized personnel only. Sessions are encrypted & logged.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
